import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Mail } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().trim().email({ message: 'Invalid email address' });

interface Shareholder {
  id: string;
  name: string;
  email: string | null;
}

interface InvestorInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  shareholders: Shareholder[];
  onSuccess?: () => void;
}

export function InvestorInviteDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  shareholders,
  onSuccess,
}: InvestorInviteDialogProps) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [shareholderId, setShareholderId] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      toast({
        title: 'Invalid email',
        description: emailValidation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    if (!shareholderId) {
      toast({
        title: 'Missing information',
        description: 'Please select a shareholder',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-investor-invite', {
        body: {
          investorEmail: email.trim(),
          shareholderId,
          companyName,
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send invitation');
      }

      toast({
        title: 'Invitation sent!',
        description: `An invitation email has been sent to ${email}`,
      });

      // Reset form
      setEmail('');
      setShareholderId('');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast({
        title: 'Error sending invitation',
        description: error.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  // Auto-fill email if shareholder is selected and has email
  const handleShareholderChange = (shareholderId: string) => {
    setShareholderId(shareholderId);
    const shareholder = shareholders.find((s) => s.id === shareholderId);
    if (shareholder?.email && !email) {
      setEmail(shareholder.email);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Investor Invitation</DialogTitle>
          <DialogDescription>
            Send a secure invitation email to grant investor portal access
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="shareholder">Shareholder *</Label>
            <Select
              value={shareholderId}
              onValueChange={handleShareholderChange}
              disabled={sending}
            >
              <SelectTrigger id="shareholder">
                <SelectValue placeholder="Select shareholder..." />
              </SelectTrigger>
              <SelectContent>
                {shareholders.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} {s.email ? `(${s.email})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="investor-email">Investor Email *</Label>
            <Input
              id="investor-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="investor@example.com"
              disabled={sending}
              required
            />
            <p className="text-xs text-muted-foreground">
              A magic link will be sent to this email for secure login
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">What happens next?</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Investor receives a branded invitation email</li>
                  <li>• They can access the portal via a secure magic link</li>
                  <li>• Access is automatically mapped to their shareholdings</li>
                </ul>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={sending || !email || !shareholderId}>
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
